import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "../Logo";
import { NAV } from "../../data/site";
import { CTAButton } from "../ui-regera/CTAButton";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
    <header
      data-testid="site-header"
      data-menu-open={open ? "true" : "false"}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled || open
          ? "bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-gradient-to-b from-[#0a0a0a]/95 via-[#0a0a0a]/55 to-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-[72px] lg:h-[84px]">
          <Logo size="header" className="shrink-0" />

          <nav className="hidden xl:flex items-center gap-4 2xl:gap-7 mx-4" aria-label="Primary">
            {NAV.slice(1, 9).map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`link-sweep text-[11px] font-medium tracking-[0.16em] transition-colors duration-300 ${
                    active ? "text-[#e60000]" : "text-white/70 hover:text-white"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <CTAButton
              to="/contact#enquiry"
              testId="header-engage-cta"
              className="hidden sm:inline-flex !px-5 !py-2.5 text-[11px]"
            >
              ENGAGE REGERA
            </CTAButton>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              className="xl:hidden text-white p-2 -mr-2"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="xl:hidden fixed left-0 right-0 top-[72px] lg:top-[84px] bottom-0 z-40 bg-[#0a0a0a] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-5 sm:px-8 py-8 flex flex-col">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="group flex items-center justify-between border-b border-white/10 py-5"
                  >
                    <span className="font-display text-2xl font-bold tracking-tight text-white group-hover:text-[#e60000] transition-colors">
                      {item.label}
                    </span>
                    <ArrowUpRight className="h-5 w-5 text-white/30 group-hover:text-[#e60000]" />
                  </Link>
                </motion.div>
              ))}
              <CTAButton to="/contact#enquiry" testId="mobile-engage-cta" className="mt-8 w-full">
                ENGAGE REGERA
              </CTAButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
